import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../ducks/auth.duck';
import VerifyEmailBanner from '../VerifyEmailBanner/VerifyEmailBanner';
import { getCartCount } from '../../util/cart';
import logoImg from '../../assets/dhobara-logo.png';
import css from './DhobaraSiteNav.module.css';

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconChevron = () => (
  <svg viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="7">
    <path d="M1 1l4 4 4-4" />
  </svg>
);

// ── Data ──────────────────────────────────────────────────────────────────────

const COLLECTIONS = [
  { label: 'Bridal', to: '/collection/bridal' },
  { label: 'Formal', to: '/collection/formal' },
  { label: 'Casual', to: '/collection/casual' },
  { label: 'Mens', to: '/collection/mens' },
  { label: 'Kids', to: '/collection/kids' },
];

// ── Component ─────────────────────────────────────────────────────────────────

const DhobaraSiteNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const closeMenu = () => { setMobileMenuOpen(false); setCollectionsOpen(false); };

  const dispatch = useDispatch();
  const history = useHistory();

  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUser = useSelector(state => state.user?.currentUser);
  const displayName = currentUser?.attributes?.profile?.firstName || null;

  const [cartCount, setCartCount] = useState(0);
  useEffect(() => {
    const update = () => setCartCount(getCartCount());
    update();
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);

  const handleLogout = () => {
    dispatch(logout()).then(() => {
      history.push('/');
    });
  };

  return (
    <>
    <VerifyEmailBanner />
    <header className={css.nav}>
      <Link to="/" className={css.navLogo}>
        <img src={logoImg} alt="Dhobara" />
      </Link>

      {/* Desktop nav */}
      <nav>
        <ul className={css.navLinks}>
          <li><Link to="/how-it-works">How It Works</Link></li>
          <li className={css.navDropdownWrapper}>
            <button type="button" className={css.navDropdownTrigger}>
              Collections <IconChevron />
            </button>
            <div className={css.navDropdownMenu}>
              {COLLECTIONS.map(c => (
                <Link key={c.to} to={c.to} className={css.navDropdownItem}>{c.label}</Link>
              ))}
              <Link to="/s" className={css.navDropdownItem}>Browse All</Link>
            </div>
          </li>
          <li><Link to="/designers">Designers</Link></li>
          <li><Link to="/why-dhobara">Why Dhobara?</Link></li>
        </ul>
      </nav>

      {/* Right side */}
      <div className={css.navRight}>
        <Link to={isAuthenticated ? '/cart' : '/login'} className={css.cartIcon} aria-label="Saved items">
          <span className={css.cartIconWrap}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {cartCount > 0 && <span className={css.cartBadge}>{cartCount}</span>}
          </span>
          <span className={css.cartLabel}>Saved Items</span>
        </Link>
        <Link to="/dashboard" className={css.navCta}>Sell With Us</Link>
        {isAuthenticated ? (
          <div className={css.userMenuWrapper}>
            <button type="button" className={css.userMenuTrigger}>
              {displayName ? `Hi, ${displayName}` : 'Account'} <IconChevron />
            </button>
            <div className={css.userMenuDropdown}>
              <Link to="/dashboard" className={css.navDropdownItem}>Dashboard</Link>
              <Link to="/account" className={css.navDropdownItem}>Account Settings</Link>
              <div className={css.navDropdownDivider} />
              <button type="button" className={css.navDropdownItemBtn} onClick={handleLogout}>
                Log Out
              </button>
            </div>
          </div>
        ) : (
          <>
            <Link to="/signup" className={css.navAuthLink}>Sign Up</Link>
            <Link to="/login" className={css.navAuthLink}>Log In</Link>
          </>
        )}
        <button
          type="button"
          className={css.navMenuBtn}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(open => !open)}
        >
          {mobileMenuOpen ? <IconClose /> : <IconMenu />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={css.mobileMenu}>
          <Link to="/how-it-works" onClick={closeMenu}>How It Works</Link>
          <div className={css.mobileCollectionsGroup}>
            <button
              type="button"
              className={css.mobileCollectionsTrigger}
              onClick={() => setCollectionsOpen(o => !o)}
            >
              <span>Collections</span>
              <span className={collectionsOpen ? css.mobileChevronOpen : css.mobileChevron}>
                <IconChevron />
              </span>
            </button>
            {collectionsOpen && (
              <div className={css.mobileCollectionsDrawer}>
                {COLLECTIONS.map(c => (
                  <Link key={c.to} to={c.to} onClick={closeMenu} className={css.mobileCollectionItem}>{c.label}</Link>
                ))}
                <Link to="/s" onClick={closeMenu} className={css.mobileCollectionItem}>Browse All</Link>
              </div>
            )}
          </div>
          <Link to="/designers" onClick={closeMenu}>Designers</Link>
          <Link to="/why-dhobara" onClick={closeMenu}>Why Dhobara?</Link>
          <Link to="/cart" onClick={closeMenu}>Saved Items</Link>
          <div className={css.mobileMenuDivider} />
          <Link to="/dashboard" onClick={closeMenu}>Sell With Us</Link>
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" onClick={closeMenu}>Dashboard</Link>
              <Link to="/account" onClick={closeMenu}>Account Settings</Link>
              <button
                type="button"
                className={css.mobileMenuBtn}
                onClick={() => { closeMenu(); handleLogout(); }}
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/signup" onClick={closeMenu}>Sign Up</Link>
              <Link to="/login" onClick={closeMenu}>Log In</Link>
            </>
          )}
        </div>
      )}
    </header>
    </>
  );
};

export default DhobaraSiteNav;
